import {
  CButton,
  CLoadingButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
} from "@coreui/react-pro";
import { useEffect, useState } from "react";
import { formatToReadableDate } from "../../utils/utils";
import AppReleaseModal from "./AppReleaseModal";
import toast, { Toaster } from "react-hot-toast";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AppRelease = ({ Language: lang }) => {
  const [releases, setReleases] = useState([]);
  const [formVal, setFormVal] = useState({
    name: "",
    description: "",
    hasBreakingChanges: "no", // "yes" or "no"
  });
  const [upgradeMessageState, setUpgradeMessageState] = useState({
    visible: false,
    label: "",
    description: "",
    loading: false,
  });

  const [State, setState] = useState({
    visible: false,
    loading: false,
    Modeltitel: lang === "en" ? "Add New Release" : "添加新发布",
    ModelButton: lang === "en" ? "Create" : "创建",
    DltVisible: false,
    DltLoading: false,
    selectedId: null,
    isEditMode: false,
  });

  const getAllReleases = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/app-release/all`);
      const result = await response.json();
      setReleases(result);
    } catch (error) {
      console.error("Fetch error:", error.message);
    }
  };
  const fetchUpgradeMessage = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/app-release/upgrade-message`);
      const result = await res.json();
      if (res.ok) {
        setUpgradeMessageState((prev) => ({
          ...prev,
          label: result.Label || "",
          description: result.Description || "",
        }));
      }
    } catch (err) {
      console.error("Upgrade message fetch error:", err.message);
    }
  };

  const handleSubmitUpgradeMessage = async () => {
    try {
      setUpgradeMessageState((prev) => ({
        ...prev,
        loading: true,
      }));

      const res = await fetch(`${BASE_URL}/api/app-release/upgrade-message`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: upgradeMessageState.label,
          description: upgradeMessageState.description,
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      toast.success(
        lang === "en" ? "Upgrade message updated!" : "升级信息已更新！"
      );
      setUpgradeMessageState((prev) => ({
        ...prev,
        visible: false,
      }));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpgradeMessageState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  useEffect(() => {
    getAllReleases();
    fetchUpgradeMessage();
  }, []);

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setFormVal((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const payload = {
        name: formVal.name,
        description: formVal.description,
        isBreaking: formVal.hasBreakingChanges === "yes",
      };

      const url = State.isEditMode
        ? `${BASE_URL}/api/app-release/${State.selectedId}`
        : `${BASE_URL}/api/app-release`;

      const method = State.isEditMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (!res.ok) throw new Error(result.message || result.Error);

      getAllReleases();
      handleCloseModal();

      toast.success(result.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleCloseModal = () => {
    setState((prev) => ({
      ...prev,
      visible: false,
      DltVisible: false,
      loading: false,
      DltLoading: false,
      selectedId: null,
      isEditMode: false, // reset edit mode
    }));
    setFormVal({
      name: "",
      description: "",
      hasBreakingChanges: "no",
    });
  };

  const handleChangeDelete = async () => {
    try {
      setState((prev) => ({ ...prev, DltLoading: true }));

      const res = await fetch(
        `${BASE_URL}/api/app-release/${State.selectedId}`,
        {
          method: "DELETE",
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result?.message || "Delete failed");

      toast.success(lang === "en" ? "Deleted successfully!" : "删除成功！");
      getAllReleases();
      handleCloseModal();
    } catch (err) {
      toast.error(err.message);
      setState((prev) => ({ ...prev, DltLoading: false }));
    }
  };

  const columns = [
    {
      key: "id",
      label: "ID",
      filter: false,
      sorter: false,
      _style: { width: "5%" },
    },
    {
      key: "Name",
      label: lang === "en" ? "Version Name" : "版本名称",
      _style: { width: "20%" },
      filter: true,
      sorter: true,
    },
    {
      key: "Description",
      label: lang === "en" ? "Description" : "描述",
      _style: { width: "35%" },
      filter: true,
      sorter: true,
    },
    {
      key: "IsBreakingVersion",
      label: lang === "en" ? "Breaking Version" : "重大版本",
      _style: { width: "15%" },
      filter: true,
      sorter: true,
    },

    {
      key: "CreatedAt",
      label: lang === "en" ? "Created At" : "创建时间",
      _style: { width: "15%" },
      filter: true,
      sorter: true,
    },
    {
      key: "actions",
      label: lang === "en" ? "Actions" : "操作",
      _style: { width: "15%" },
    },
  ];

  return (
    <>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="h3 mb-2 text-gray-800 HeadTitles">
            {lang === "en" ? "App Release" : "应用发布"}
          </h1>
          <div className="">
            <CButton
              color="danger"
              className="mr-2"
              onClick={() =>
                setUpgradeMessageState((prev) => ({ ...prev, visible: true }))
              }
            >
              {lang === "en" ? "Upgrade Message" : "升级信息"}
            </CButton>
            <CButton
              color="success"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  visible: true,
                  Modeltitel: lang === "en" ? "Add New Release" : "添加新发布",
                  ModelButton: lang === "en" ? "Create" : "创建",
                }))
              }
            >
              + {lang === "en" ? "Add New Release" : "添加新发布"}
            </CButton>
          </div>
        </div>

        <br />

        <div className="card shadow mb-4">
          <div className="card-body">
            <div className="mb-12 VendorTable">
              <CSmartTable
                activePage={1}
                responsive="true"
                tableFilter
                tableFilterPlaceholder={lang === "en" ? "Search..." : "搜索..."}
                tableFilterLabel={[]}
                columns={columns}
                columnSorter
                loading={false}
                items={releases}
                itemsPerPageSelect
                itemsPerPage={10}
                itemsPerPageLabel={
                  lang === "en" ? "Items per page:" : "每页数量:"
                }
                pagination
                tableProps={{
                  hover: true,
                  responsive: true,
                  striped: true,
                }}
                tableBodyProps={{
                  className: "align-middle",
                }}
                scopedColumns={{
                  id: (item, i) => <td>{i + 1}.</td>,
                  IsBreakingVersion: (item) => (
                    <td>{item.IsBreakingVersion ? "Yes" : "No"}</td>
                  ),
                  CreatedAt: (item) => (
                    <td>{formatToReadableDate(item.CreatedAt)}</td>
                  ),
                  actions: (item) => (
                    <td className="d-flex gap-2 ">
                      <span className="m-1">
                        <CButton
                          size="sm"
                          color="primary"
                          onClick={() => {
                            setFormVal({
                              name: item.Name,
                              description: item.Description,
                              hasBreakingChanges: item.IsBreakingVersion
                                ? "yes"
                                : "no",
                            });
                            setState((prev) => ({
                              ...prev,
                              visible: true,
                              isEditMode: true,
                              selectedId: item.ID,
                              Modeltitel:
                                lang === "en"
                                  ? "Update Release"
                                  : "更新发布版本",
                              ModelButton: lang === "en" ? "Update" : "更新",
                            }));
                          }}
                        >
                          {lang === "en" ? "Edit" : "编辑"}
                        </CButton>
                      </span>
                      <span className="m-1">
                        <CButton
                          size="sm"
                          color="danger"
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              DltVisible: true,
                              selectedId: item.ID,
                            }))
                          }
                        >
                          {lang === "en" ? "Delete" : "删除"}
                        </CButton>
                      </span>
                    </td>
                  ),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <AppReleaseModal
        lang={lang}
        State={State}
        formVal={formVal}
        handleChangeInput={handleChangeInput}
        handleCloseModal={handleCloseModal}
        handleSubmit={handleSubmit}
      />
      <CModal visible={State.DltVisible} onClosePrevented={handleCloseModal}>
        <CModalHeader>
          <CModalTitle>
            {lang === "en" ? "Delete Confirmation" : "删除确认"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {lang === "en"
            ? "Are you sure you wish to delete this release?"
            : "你确认要删除这个发布版本吗？"}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleCloseModal}>
            {lang === "en" ? "Close" : "关闭"}
          </CButton>
          {State.DltLoading ? (
            <CLoadingButton color="danger" disabled />
          ) : (
            <CButton color="danger" onClick={handleChangeDelete}>
              {lang === "en" ? "Confirm Delete" : "确认删除"}
            </CButton>
          )}
        </CModalFooter>
      </CModal>

      {/*  */}
      <CModal
        visible={upgradeMessageState.visible}
        onClose={() =>
          setUpgradeMessageState((prev) => ({ ...prev, visible: false }))
        }
      >
        <CModalHeader>
          <CModalTitle>
            {lang === "en" ? "Edit Upgrade Message" : "编辑升级信息"}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div className="mb-3">
            <label className="form-label">
              {lang === "en" ? "Label" : "标签"}
            </label>
            <input
              type="text"
              className="form-control"
              value={upgradeMessageState.label}
              onChange={(e) =>
                setUpgradeMessageState((prev) => ({
                  ...prev,
                  label: e.target.value,
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {lang === "en" ? "Description" : "描述"}
            </label>
            <textarea
              className="form-control"
              rows={5}
              value={upgradeMessageState.description}
              onChange={(e) =>
                setUpgradeMessageState((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            ></textarea>
          </div>
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            onClick={() =>
              setUpgradeMessageState((prev) => ({ ...prev, visible: false }))
            }
          >
            {lang === "en" ? "Cancel" : "取消"}
          </CButton>
          {upgradeMessageState.loading ? (
            <CLoadingButton color="primary" disabled />
          ) : (
            <CButton color="primary" onClick={handleSubmitUpgradeMessage}>
              {lang === "en" ? "Save" : "保存"}
            </CButton>
          )}
        </CModalFooter>
      </CModal>

      {/*  */}

      <Toaster />
    </>
  );
};

export default AppRelease;
